import { action, observable, makeObservable } from "mobx";
import UserAPI from "../api/UserAPI";
import { User, UserList } from "../models/User/User";
import { EmployeeRoles } from "../models/Employees/EmployeeRoles";
import { GerUserParams } from "../models/User/GetUserParams";
import { FilterValue } from "../models/Filters/FilterValue";
import { APIResponse } from "../models/NetworkResponse";
import { ActivityLog } from "../models/Activity/ActivityLog";

const RECOVERY_RESEND_STORAGE_PREFIX = "user-recovery-resend-at:";

interface RecoveryLinkSendPayload {
  email?: string;
  linkExpiresInHours?: number;
  nextResendAvailableAt?: string;
}

const getRecoveryResendStorageKey = (userId: number) =>
  `${RECOVERY_RESEND_STORAGE_PREFIX}${userId}`;

const parseRecoveryLinkSendPayload = (
  response: unknown,
): RecoveryLinkSendPayload => {
  const root = response as Record<string, unknown> | undefined;
  const payload =
    (root?.data as RecoveryLinkSendPayload | undefined) ??
    (root?.result as RecoveryLinkSendPayload | undefined) ??
    (root as RecoveryLinkSendPayload | undefined);

  return {
    email: payload?.email,
    linkExpiresInHours: payload?.linkExpiresInHours,
    nextResendAvailableAt: payload?.nextResendAvailableAt,
  };
};

const formatResendCountdown = (millisecondsRemaining: number) => {
  const totalSeconds = Math.max(0, Math.ceil(millisecondsRemaining / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

export class UserStore {
  userAPI: UserAPI;
  user?: User;
  userDetails: UserList;
  originalUser: UserList;
  limit: number;
  total_pages: number;
  total_count: number;
  users: UserList[];
  isLoggedIn: Boolean;
  loading: boolean;
  permissionMap: Map<string, string>;
  ids: number[];
  resetOtp: string;
  resetNewPassword: string;
  resetConfirmPassword: string;
  resetErrors: string[];
  resetLoading: boolean;
  isSendingRecoveryEmail: boolean;
  recoveryResendAvailableAt: string | null;
  recoveryResendUserId: number | null;
  recoveryResendNow: number;
  activityLogs: ActivityLog[];

  constructor() {
    this.userAPI = new UserAPI();
    this.isLoggedIn = false;
    this.loading = false;
    this.permissionMap = new Map<string, string>();
    this.limit = 0;
    this.total_pages = 0;
    this.total_count = 0;
    this.userDetails = {} as UserList;
    this.originalUser = {} as UserList;
    this.users = [];
    this.ids = [];

    this.resetOtp = "";
    this.resetNewPassword = "";
    this.resetConfirmPassword = "";
    this.resetErrors = [];
    this.resetLoading = false;
    this.isSendingRecoveryEmail = false;
    this.recoveryResendAvailableAt = null;
    this.recoveryResendUserId = null;
    this.recoveryResendNow = Date.now();
    this.activityLogs = [];

    makeObservable(this, {
      users: observable,
      userDetails: observable,
      user: observable,
      isLoggedIn: observable,
      loading: observable,
      login: action,
      updateFirstName: action,
      discardChanges: action,
      deleteUser: action,
      updateLocalCustomerDetails: action,
      updatePassword: action,
      getUserDetails: action,
      resetPassword: action,
      logOut: action,
      resetUser: action,
      reset: action,
      getUserDetailsById: action,
      verifyLoginOtp: action,
      resetOtp: observable,
      resetNewPassword: observable,
      resetConfirmPassword: observable,
      resetErrors: observable,
      resetLoading: observable,
      isSendingRecoveryEmail: observable,
      recoveryResendAvailableAt: observable,
      recoveryResendUserId: observable,
      recoveryResendNow: observable,
      setResetOtp: action,
      setResetNewPassword: action,
      setResetConfirmPassword: action,
      setResetErrors: action,
      setResetLoading: action,
      setIsSendingRecoveryEmail: action,
      loadRecoveryResendCooldown: action,
      setRecoveryResendCooldown: action,
      clearRecoveryResendCooldown: action,
      tickRecoveryResendNow: action,
      resetResetPasswordForm: action,
      confirmResetPassword: action,
      sendLoginOtp: action,
      updateCustomerName: action,
      updateUserDetails: action,
      sendCustomerPasswordRecoveryLink: action,
      searchIds: action,
      activityLogs: observable,
      getUserActivityLogs: action,
      clearActivityLogs: action,
    });
  }

  reset = () => {
    this.userAPI = new UserAPI();
    this.isLoggedIn = false;
    this.loading = false;
    this.permissionMap = new Map<string, string>();
    Object.assign(this, new UserStore());
  };

  resetResetPasswordForm = () => {
    this.resetOtp = "";
    this.resetNewPassword = "";
    this.resetConfirmPassword = "";
    this.resetErrors = [];
    this.resetLoading = false;
  };

  setResetOtp = (otp: string) => {
    this.resetOtp = otp;
  };

  setResetNewPassword = (password: string) => {
    this.resetNewPassword = password;
  };

  setResetConfirmPassword = (confirmPassword: string) => {
    this.resetConfirmPassword = confirmPassword;
  };

  setResetErrors = (errors: string[]) => {
    this.resetErrors = errors;
  };

  setResetLoading = (loading: boolean) => {
    this.resetLoading = loading;
  };

  setIsSendingRecoveryEmail = (loading: boolean) => {
    this.isSendingRecoveryEmail = loading;
  };

  loadRecoveryResendCooldown = (userId: number) => {
    const storedResendAt = localStorage.getItem(getRecoveryResendStorageKey(userId));

    if (!storedResendAt) {
      if (this.recoveryResendUserId === userId) {
        this.recoveryResendAvailableAt = null;
        this.recoveryResendUserId = null;
      }
      return;
    }

    if (new Date(storedResendAt).getTime() <= Date.now()) {
      this.clearRecoveryResendCooldown(userId);
      return;
    }

    this.recoveryResendAvailableAt = storedResendAt;
    this.recoveryResendUserId = userId;
    this.recoveryResendNow = Date.now();
  };

  setRecoveryResendCooldown = (
    userId: number,
    nextResendAvailableAt: string,
  ) => {
    this.recoveryResendAvailableAt = nextResendAvailableAt;
    this.recoveryResendUserId = userId;
    this.recoveryResendNow = Date.now();
    localStorage.setItem(
      getRecoveryResendStorageKey(userId),
      nextResendAvailableAt,
    );
  };

  clearRecoveryResendCooldown = (userId: number) => {
    localStorage.removeItem(getRecoveryResendStorageKey(userId));
    if (this.recoveryResendUserId === userId) {
      this.recoveryResendAvailableAt = null;
      this.recoveryResendUserId = null;
    }
  };

  tickRecoveryResendNow = () => {
    this.recoveryResendNow = Date.now();

    if (
      this.recoveryResendAvailableAt &&
      this.recoveryResendUserId &&
      new Date(this.recoveryResendAvailableAt).getTime() <= this.recoveryResendNow
    ) {
      this.clearRecoveryResendCooldown(this.recoveryResendUserId);
    }
  };

  isRecoveryResendOnCooldownForUser = (userId: number) => {
    if (
      !this.recoveryResendAvailableAt ||
      this.recoveryResendUserId !== userId
    ) {
      return false;
    }

    return (
      new Date(this.recoveryResendAvailableAt).getTime() > this.recoveryResendNow
    );
  };

  getRecoveryResendCountdownLabel = (userId: number) => {
    if (!this.isRecoveryResendOnCooldownForUser(userId)) {
      return null;
    }

    return formatResendCountdown(
      new Date(this.recoveryResendAvailableAt!).getTime() -
        this.recoveryResendNow,
    );
  };

  confirmResetPassword = async (
    recoveryToken: string,
    otp: string,
    password: string,
  ): Promise<APIResponse> => {
    this.setResetLoading(true);
    this.setResetErrors([]);
    try {
      const res = await this.userAPI.setNewPassword(
        recoveryToken,
        otp,
        password,
      );
      return res;
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        "Failed to reset password. Please try again later.";
      this.setResetErrors([msg]);
      throw error;
    } finally {
      this.setResetLoading(false);
    }
  };

  resetUser = () => {
    this.userDetails = {} as UserList;
    this.originalUser = {} as UserList;
  };

  login = async (email: string, password: string) => {
    try {
      this.loading = true;
      this.user = await this.userAPI.login(email, password);
      if (this.user.token) {
        this.isLoggedIn = true;
        const tokenKey =
          process.env.REACT_APP_TOKEN_KEY || "Bearer_token#localhost";
        localStorage.setItem(tokenKey, this.user.token);
      }
    } catch (error) {
      return false;
    } finally {
      this.loading = false;
    }
  };

  // checkLogIn = async () => {
  //   try {
  //     this.isLoggedIn = await this.userAPI.checkLogin();
  //   } catch (error) {
  //     throw error;
  //   }
  // };

  getUserDetails = async () => {
    try {
      const user = await this.userAPI.getUserDetails();
      this.user = user;
      this.user.permissions?.forEach((permission) => {
        this.permissionMap.set(permission.identifier, permission.role);
      });
      this.isLoggedIn = true;
    } catch (error) {
      this.isLoggedIn = false;
      throw error;
    }
  };

  resetPassword = async (email: string) => {
    try {
      const response = await this.userAPI.resetPassword(email);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  logOut = async () => {
    try {
      await this.userAPI.logOut();
    } catch (error) { }
  };

  hasPermission = (
    role: EmployeeRoles | EmployeeRoles[],
    ignoreAllPermission: boolean = false,
  ): boolean => {
    let hasAccess = false;

    if (this.permissionMap.size === 0) return hasAccess;
    if (this.permissionMap.get(EmployeeRoles.ALL) && !ignoreAllPermission)
      return true;

    if (Array.isArray(role)) {
      for (let i in role) {
        const item = role[i];
        const permission = this.permissionMap.get(item);
        if (permission) {
          hasAccess = true;
          break;
        }
      }
    } else {
      const permission = this.permissionMap.get(role);
      if (permission) hasAccess = true;
    }

    return hasAccess;
  };

  isSuperAdmin = (): boolean => {
    if (!this.user) return false;

    if (!this.user.teams || this.user.teams.length === 0) return false;

    const filteredList = this.user.teams.filter((team) => team.id === 1);
    if (filteredList.length === 0) return false;

    return true;
  };

  isSuperAdminManager = (): boolean => {
    if (!this.user) return false;

    if (!this.user.teams || this.user.teams.length === 0) return false;

    const filteredList = this.user.teams.filter(
      (team) => team.id === 1 && team.is_manager,
    );
    if (filteredList.length === 0) return false;

    return true;
  };

  isTeamManager = (teamId: number | number[] | undefined): boolean => {
    if (!teamId) return false;

    if (!this.user) return false;

    if (!this.user.teams || this.user.teams.length === 0) return false;

    let managerMap = new Map<number, boolean>();

    this.user.teams.forEach((team) => managerMap.set(team.id, team.is_manager));

    if (Array.isArray(teamId)) {
      for (let i in teamId) {
        const item = teamId[i];
        if (managerMap.get(item)) return true;
      }
    } else {
      if (managerMap.get(teamId)) return true;
    }

    return false;
  };

  sendLoginOtp = async (email: string, password: string) => {
    try {
      this.loading = true;
      await this.userAPI.sendOtp(email, password);
    } catch (error) {
    } finally {
      this.loading = false;
    }
  };

  verifyLoginOtp = async (email: string, password: string, otp: string) => {
    try {
      this.loading = true;
      this.user = await this.userAPI.verifyOtp(email, password, otp);
      if (this.user && this.user.token) {
        this.isLoggedIn = true;
        const tokenKey =
          process.env.REACT_APP_TOKEN_KEY || "Bearer_token#localhost";
        localStorage.setItem(tokenKey, this.user.token);
        return this.user;
      } else {
        return undefined;
      }
    } catch (error) {
      return undefined;
    } finally {
      this.loading = false;
    }
  };

  getUsers = async (filter: GerUserParams) => {
    try {
      const response = await this.userAPI.getUsers(filter);
      this.limit = response.limit;
      this.total_pages = response.total_pages;
      this.total_count = response.total_count;
      this.users = response.list;
    } catch (error: any) {
      throw error;
    }
  };

  getUserDetailsById = async (userId: number) => {
    try {
      const response = await this.userAPI.getUserDetailsById(userId);
      this.updateLocalCustomerDetails(response);
    } catch (error: any) {
      throw error;
    }
  };

  updateFirstName = (first_name: string) => {
    this.userDetails.first_name = first_name;
  };

  updateLastName = (last_name: string) => {
    this.userDetails.last_name = last_name;
  };

  // updateCompanyName = (company_name: string) => {
  //   this.userDetails.company_name = company_name;
  // };

  updateEmail = (email: string) => {
    this.userDetails.email = email;
  };

  updatePassword = (password: string) => {
    this.userDetails.password = password;
  };

  updateConfirmPassword = (confirmPassword: string) => {
    this.userDetails.confirmPassword = confirmPassword;
  };

  updateIsPhoneVerified = (phone_verified: boolean) => {
    this.userDetails.phone_verified = phone_verified;
  };

  updateIsPhoneNumber = (phone_number: string) => {
    this.userDetails.phone = phone_number;
  };

  updateIsAdmin = (is_admin: boolean) => {
    this.userDetails.is_admin = is_admin;
  };

  updateIsEmailVerified = (emailVerified: boolean) => {
    this.userDetails.email_verified = emailVerified;
  };

  updateLocalCustomerDetails = (user: UserList) => {
    this.userDetails = user;
    this.originalUser = user;
  };

  createUser = async () => {
    try {
      const userRequest: UserList = {
        customer_id: this.userDetails.customer_id,
        first_name: this.userDetails.first_name,
        last_name: this.userDetails.last_name,
        email: this.userDetails.email,
        phone: this.userDetails.phone,
        is_admin: this.userDetails.is_admin,
        send_email: false,
      };

      const response = await this.userAPI.createUser(userRequest);

      this.userDetails = response.result || [];
      return response;
    } catch (error: any) {
      console.error("Error creating customer:", error);
      throw error;
    }
  };

  sendCustomerPasswordRecoveryLink = async (userId: number) => {
    this.setIsSendingRecoveryEmail(true);
    try {
      const response =
        await this.userAPI.sendCustomerPasswordRecoveryLink(userId);
      const payload = parseRecoveryLinkSendPayload(response);

      if (payload.nextResendAvailableAt) {
        this.setRecoveryResendCooldown(userId, payload.nextResendAvailableAt);
      }

      return { response, payload };
    } catch (error: any) {
      const payload = parseRecoveryLinkSendPayload(error?.response?.data);

      if (payload.nextResendAvailableAt) {
        this.setRecoveryResendCooldown(userId, payload.nextResendAvailableAt);
      }

      throw error;
    } finally {
      this.setIsSendingRecoveryEmail(false);
    }
  };
  clearUserDetails = async () => {
    this.originalUser = {};
    this.userDetails = {};
  };

  updateUserDetails = async () => {
    try {
      if (!this.userDetails.id) return;

      const userRequest = {
        id: this.userDetails.id,
        customer_id: this.userDetails.customer_id,
        first_name: this.userDetails.first_name,
        last_name: this.userDetails.last_name,
        email: this.userDetails.email,
        is_admin: this.userDetails.is_admin,
        phone: this.userDetails.phone,
      };

      const response = await this.userAPI.updateUserDetails(userRequest);

      // this.updateLocalCustomerDetails(response.result);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  updateName = (name: string) => {
    this.userDetails.first_name = name;
  };

  deleteUser = async (userId: number) => {
    try {
      const response = await this.userAPI.deleteUser(userId);
      if (response.success && this.userDetails) {
        const index = this.users.findIndex((item) => item.id === userId);
        if (index !== -1) {
          this.users[index] = {
            ...this.users[index],
          };
        }
      }
      return response;
    } catch (error: any) {
      console.error("Error creating customer:", error);
      throw error;
    }
  };

  discardChanges = async () => {
    this.userDetails = { ...this.originalUser };
  };

  updateCustomerName = (selectedValue?: FilterValue[]) => {
    if (selectedValue && selectedValue.length !== 0)
      this.userDetails = {
        ...this.userDetails,
        customer_id: Number(selectedValue[0].id),
        customer_name: selectedValue[0].value,
      };
    else
      this.userDetails = {
        ...this.userDetails,
        customer_id: undefined,
        customer_name: undefined,
      };
  };

  searchIds = async (searchText: string) => {
    try {
      const list = await this.userAPI.getIds(searchText);
      this.ids = list;
      return this.ids;
    } catch (error: any) {
      throw error;
    }
  };

  getUserActivityLogs = async (userId: number) => {
    try {
      const response = await this.userAPI.getUserActivityLogs(userId);
      this.activityLogs = Array.isArray(response) ? response : [];
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  clearActivityLogs = () => {
    this.activityLogs = [];
  };
}
